import { clsx } from "@regrello/core-utils";
import type React from "react";

import type { RegrelloButtonVariant } from "./RegrelloButtonVariant";

export interface RegrelloButtonGroupV2Props {
  /** The label for the button group. */
  ariaLabel?: string;

  /** The buttons to show in the button group. */
  children: React.ReactNode;

  /** Ref to the button group div element. */
  ref?: React.Ref<HTMLDivElement>;

  /**
   * The variant that the child buttons are using. All buttons must use the same variant in order
   * to ensure valid group styling.
   */
  variant: RegrelloButtonVariant;
}

/**
 * A standard button group in Regrello.
 */
export function RegrelloButtonGroup({ ariaLabel, children, ref, variant }: RegrelloButtonGroupV2Props) {
  return (
    <div
      ref={ref}
      aria-label={ariaLabel}
      className={clsx([
        `
        flex
        items-center
        `,
        // (clewis): Handle the simple case in which buttons are direct children.
        `
        [&>button:not(:last-child)]:rounded-r-none
        [&>button:not(:first-child)]:rounded-l-none
        `,
        // (clewis): Handle the more complex case in which buttons are wrapped in a popover.
        `
        [&>*:not(:first-child)>button]:rounded-r-none
        [&>*:not(:last-child)>button]:rounded-r-none
        `,
        {
          [`
            [&>button:not(:first-child)]:border-l
          [&>button:not(:first-child)]:border-background/50
            [&>button:not(:first-child)]:border-solid
          `]: variant === "solid",
          [`
            [&>*:not(:first-child)]:-ml-px
          `]: variant === "outline" || variant === "surface",
        },
      ])}
      role="toolbar"
    >
      {children}
    </div>
  );
}
